import { memo, useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { Badge, Button, CartContainer, IconHighlight, ProgressBar } from '@aimsos/ui';

import { INSIGHT_TREND_VARIANTS } from '../../constants/intelligence.js';
import { iconForInsightCategory } from './insightIcons.js';

import { ChevronDown, Sparkles } from 'lucide-react';

/** A pattern losing hold, or not one yet, draws its bar in amber, as in the prototype. */
const AMBER_TRENDS = Object.freeze(['weakening', 'emerging']);

/**
 * One insight in the Insights grid (CT-5716): category icon and title, the trend
 * chip, the value chip, the headline, a progress bar when the insight has a
 * percentage, the source line, the evidence bullets and — where the payload says
 * there are attempts to look at — "View attempts".
 *
 * Not an `EntityList` row: the design is a card in a two-column grid with a
 * bar and bullets, which `EntityList`'s slots do not hold. The surface is still
 * the design system's `CartContainer`, clickable as a whole (keyboard included);
 * "View attempts" opens the same slide-out, where the sources are (CT-5716,
 * open question 1).
 *
 * A paused or emerging insight shows its note instead of its numbers.
 */
const InsightCard = memo(function InsightCard({ insight, selected = false, onOpen }) {
  const { t } = useTranslation('unified-profiles');

  const handleOpen = useCallback(() => onOpen?.(insight), [onOpen, insight]);
  const handleViewAttempts = useCallback(
    (event) => {
      event.stopPropagation();
      onOpen?.(insight);
    },
    [onOpen, insight],
  );

  return (
    <div data-testid="insight-card" data-insight-id={insight.id} data-trend={insight.trend} className="h-full">
      <CartContainer
        size="M"
        variant="neutral"
        selected={selected}
        onClick={handleOpen}
        aria-label={t('intelligence.insights.card.open', { title: insight.title })}
        className="h-full"
      >
        <div className={['flex h-full flex-col gap-3', insight.paused ? 'opacity-70' : ''].filter(Boolean).join(' ')}>
          <div className="flex items-start justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2 font-semibold text-content">
              <IconHighlight
                icon={iconForInsightCategory(insight.category)}
                size="small"
                bgColor="Purple"
                iconColor="Purple"
              />
              <span className="truncate">{insight.title}</span>
            </span>
            {insight.trend ? (
              <Badge variant={INSIGHT_TREND_VARIANTS[insight.trend] ?? 'neutral'} size="small">
                {t(`intelligence.insights.trend.${insight.trend}`)}
              </Badge>
            ) : null}
          </div>

          {insight.value ? (
            <div>
              <Badge variant="purple" size="small">
                {insight.value}
              </Badge>
            </div>
          ) : null}

          {insight.headline ? <p className="text-sm font-medium text-content">{insight.headline}</p> : null}

          {insight.progress != null ? (
            <ProgressBar
              value={insight.progress}
              tone={AMBER_TRENDS.includes(insight.trend) ? 'amber' : 'green'}
              showLabel={false}
              ariaLabel={t('intelligence.insights.card.progress')}
            />
          ) : null}

          {/* Nothing is read from a paused insight, so it names no source. */}
          {insight.sourceLine && !insight.paused ? (
            <p className="flex items-center gap-1.5 text-xs text-content-muted">
              <Sparkles size={12} aria-hidden className="shrink-0" />
              {insight.sourceLine}
            </p>
          ) : null}

          {insight.evidence.length ? (
            <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-content-secondary">
              {insight.evidence.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          ) : null}

          {insight.note ? <p className="text-sm text-content-muted">{insight.note}</p> : null}

          {insight.hasAttempts ? (
            <div className="mt-auto flex justify-end">
              <Button variant="ghost" size="sm" rightIcon={ChevronDown} onClick={handleViewAttempts}>
                {t('intelligence.insights.card.viewAttempts')}
              </Button>
            </div>
          ) : null}
        </div>
      </CartContainer>
    </div>
  );
});

export default InsightCard;
